import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

export type FolderStructureType = 'library' | 'documents' | 'blueprints' | 'dialogs' | 'flows' | 'variables';

interface FolderStructureState {
  activeFolderTypes: Set<FolderStructureType>;
  selectedFolders: Map<FolderStructureType, string | null>;
  expandedTypes: Set<FolderStructureType>;
  userCollapsed: boolean;
  savedFolderTypes?: Set<FolderStructureType>; // Save folder types when user collapses
  availableFolderTypes: Set<FolderStructureType>; // All folder types available based on license
}

interface FolderStructureVisibilityContextType {
  // New multi-select API
  activeFolderTypes: Set<FolderStructureType>;
  toggleFolderType: (type: FolderStructureType) => void;
  isFolderTypeActive: (type: FolderStructureType) => boolean;
  addFolderTypes: (types: FolderStructureType[]) => void; // Add multiple types at once
  setAllFolderTypes: (types: FolderStructureType[]) => void; // Set all folder types at once
  
  // Available folder types (based on license)
  availableFolderTypes: Set<FolderStructureType>;
  setAvailableFolderTypes: (types: FolderStructureType[]) => void;
  
  // Per-type folder selection
  selectedFolders: Map<FolderStructureType, string | null>;
  setSelectedFolderForType: (type: FolderStructureType, folderId: string | null) => void;
  getSelectedFolderForType: (type: FolderStructureType) => string | null;
  
  // Per-type expand/collapse
  expandedTypes: Set<FolderStructureType>;
  toggleExpanded: (type: FolderStructureType) => void;
  isTypeExpanded: (type: FolderStructureType) => boolean;
  
  // User collapse state
  userCollapsed: boolean;
  resetUserCollapsed: () => void;
  
  // Computed property
  isFolderStructureVisible: boolean;
  
  // Legacy compatibility (deprecated but kept for backward compatibility)
  activeFolderType: FolderStructureType | null;
  setActiveFolderType: (type: FolderStructureType | null) => void;
  setIsFolderStructureVisible: (visible: boolean) => void;
  toggleFolderStructure: () => void;
  showFolderStructure: (type: FolderStructureType) => void;
  hideFolderStructure: () => void;
}

const FolderStructureVisibilityContext = createContext<FolderStructureVisibilityContextType | null>(null);

export const useFolderStructureVisibility = () => {
  const context = useContext(FolderStructureVisibilityContext);
  if (!context) {
    throw new Error('useFolderStructureVisibility must be used within a FolderStructureVisibilityProvider');
  }
  return context;
};

interface FolderStructureVisibilityProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'locanest_folder_structure_state';

// Helper to load state from localStorage
const loadStateFromStorage = (): Partial<FolderStructureState> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    
    const parsed = JSON.parse(stored);
    return {
      activeFolderTypes: new Set(parsed.activeFolderTypes || []),
      selectedFolders: new Map(Object.entries(parsed.selectedFolders || {}) as [FolderStructureType, string | null][]),
      expandedTypes: new Set(parsed.expandedTypes || []),
      userCollapsed: parsed.userCollapsed ?? false,
      savedFolderTypes: parsed.savedFolderTypes ? new Set(parsed.savedFolderTypes) : undefined,
      availableFolderTypes: new Set(parsed.availableFolderTypes || []),
    };
  } catch (error) {
    console.error('Failed to load folder structure state from localStorage:', error);
    return {};
  }
};

// Helper to save state to localStorage (debounced)
let saveTimeout: NodeJS.Timeout | null = null;
const saveStateToStorage = (state: FolderStructureState) => {
  if (saveTimeout) clearTimeout(saveTimeout);
  
  saveTimeout = setTimeout(() => {
    try {
      const toSave = {
        activeFolderTypes: Array.from(state.activeFolderTypes),
        selectedFolders: Object.fromEntries(state.selectedFolders),
        expandedTypes: Array.from(state.expandedTypes),
        userCollapsed: state.userCollapsed,
        savedFolderTypes: state.savedFolderTypes ? Array.from(state.savedFolderTypes) : undefined,
        availableFolderTypes: Array.from(state.availableFolderTypes),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (error) {
      console.error('Failed to save folder structure state to localStorage:', error);
    }
  }, 500); // Debounce for 500ms
};

export const FolderStructureVisibilityProvider: React.FC<FolderStructureVisibilityProviderProps> = ({ children }) => {
  // Initialize state from localStorage or defaults
  const [activeFolderTypes, setActiveFolderTypes] = useState<Set<FolderStructureType>>(() => {
    const loaded = loadStateFromStorage();
    return loaded.activeFolderTypes || new Set();
  });
  
  const [selectedFolders, setSelectedFolders] = useState<Map<FolderStructureType, string | null>>(() => {
    const loaded = loadStateFromStorage();
    return loaded.selectedFolders || new Map();
  });
  
  const [expandedTypes, setExpandedTypes] = useState<Set<FolderStructureType>>(() => {
    const loaded = loadStateFromStorage();
    return loaded.expandedTypes || new Set();
  });

  const [userCollapsed, setUserCollapsed] = useState<boolean>(() => {
    const loaded = loadStateFromStorage();
    return loaded.userCollapsed ?? false;
  });
  
  const [savedFolderTypes, setSavedFolderTypes] = useState<Set<FolderStructureType> | undefined>(() => {
    const loaded = loadStateFromStorage();
    return loaded.savedFolderTypes;
  });
  
  const [availableFolderTypes, setAvailableFolderTypesState] = useState<Set<FolderStructureType>>(() => {
    const loaded = loadStateFromStorage();
    return loaded.availableFolderTypes || new Set();
  });
  
  // Expose setUserCollapsed through context for manual resets if needed
  const resetUserCollapsed = useCallback(() => {
    setUserCollapsed(false);
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    saveStateToStorage({ activeFolderTypes, selectedFolders, expandedTypes, userCollapsed, savedFolderTypes, availableFolderTypes });
  }, [activeFolderTypes, selectedFolders, expandedTypes, userCollapsed, savedFolderTypes, availableFolderTypes]);

  // Toggle a folder type on/off
  const toggleFolderType = useCallback((type: FolderStructureType) => {
    setActiveFolderTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
        // Don't auto-expand - let user manually expand if needed
      }
      return newSet;
    });
  }, []);

  // Add multiple folder types at once
  const addFolderTypes = useCallback((types: FolderStructureType[]) => {
    setActiveFolderTypes(prev => {
      const newSet = new Set(prev);
      types.forEach(type => newSet.add(type));
      return newSet;
    });
  }, []);

  // Set all folder types at once (replaces current set)
  const setAllFolderTypes = useCallback((types: FolderStructureType[]) => {
    setActiveFolderTypes(new Set(types));
  }, []);

  // Set available folder types (based on license)
  const setAvailableFolderTypes = useCallback((types: FolderStructureType[]) => {
    setAvailableFolderTypesState(new Set(types));
  }, []);

  // Check if a folder type is active
  const isFolderTypeActive = useCallback((type: FolderStructureType) => {
    return activeFolderTypes.has(type);
  }, [activeFolderTypes]);

  // Set selected folder for a specific type
  const setSelectedFolderForType = useCallback((type: FolderStructureType, folderId: string | null) => {
    setSelectedFolders(prev => {
      const newMap = new Map(prev);
      newMap.set(type, folderId);
      return newMap;
    });
  }, []);

  // Get selected folder for a specific type
  const getSelectedFolderForType = useCallback((type: FolderStructureType): string | null => {
    return selectedFolders.get(type) || null;
  }, [selectedFolders]);

  // Toggle expand/collapse for a type
  const toggleExpanded = useCallback((type: FolderStructureType) => {
    setExpandedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  }, []);

  // Check if a type is expanded
  const isTypeExpanded = useCallback((type: FolderStructureType) => {
    return expandedTypes.has(type);
  }, [expandedTypes]);

  // Computed: folder structure is visible if any types are active
  const isFolderStructureVisible = activeFolderTypes.size > 0;

  // Legacy compatibility methods
  const activeFolderType = activeFolderTypes.size > 0 ? Array.from(activeFolderTypes)[0] : null;
  
  const setActiveFolderType = useCallback((type: FolderStructureType | null) => {
    if (type === null) {
      setActiveFolderTypes(new Set());
    } else {
      setActiveFolderTypes(new Set([type]));
      setExpandedTypes(prev => {
        const newSet = new Set(prev);
        newSet.add(type);
        return newSet;
      });
    }
  }, []);

  const setIsFolderStructureVisible = useCallback((visible: boolean) => {
    if (!visible) {
      setActiveFolderTypes(new Set());
    }
  }, []);

  const toggleFolderStructure = useCallback(() => {
    // Toggle the userCollapsed state
    setUserCollapsed(prev => {
      const newCollapsed = !prev;
      if (newCollapsed) {
        // User is collapsing - clear all active folder types
        setActiveFolderTypes(new Set());
      } else {
        // User is un-collapsing - show ALL available folder types
        setActiveFolderTypes(new Set(availableFolderTypes));
      }
      return newCollapsed;
    });
  }, [availableFolderTypes]);

  const showFolderStructure = useCallback((type: FolderStructureType) => {
    setActiveFolderTypes(new Set([type]));
    setExpandedTypes(prev => {
      const newSet = new Set(prev);
      newSet.add(type);
      return newSet;
    });
  }, []);

  const hideFolderStructure = useCallback(() => {
    setActiveFolderTypes(new Set());
  }, []);

  return (
    <FolderStructureVisibilityContext.Provider
      value={{
        // New API
        activeFolderTypes,
        toggleFolderType,
        isFolderTypeActive,
        addFolderTypes,
        setAllFolderTypes,
        availableFolderTypes,
        setAvailableFolderTypes,
        selectedFolders,
        setSelectedFolderForType,
        getSelectedFolderForType,
        expandedTypes,
        toggleExpanded,
        isTypeExpanded,
        userCollapsed,
        resetUserCollapsed,
        isFolderStructureVisible,
        // Legacy API
        activeFolderType,
        setActiveFolderType,
        setIsFolderStructureVisible,
        toggleFolderStructure,
        showFolderStructure,
        hideFolderStructure,
      }}
    >
      {children}
    </FolderStructureVisibilityContext.Provider>
  );
};

